import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, from, switchMap, take, throwError } from 'rxjs';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

let isRefreshing = false;
const refreshDone$ = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const authService = inject(AuthService);
  const router = inject(Router);

  return from(storageService.getStorage('token')).pipe(
    switchMap(tokenResult => {
      const token = tokenResult?.value;
      const authReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;

      return next(authReq).pipe(
        catchError(err => {
          if (err.status === 401 && !req.url.includes('auth/refresh') && !req.url.includes('auth/branchlogin')) {
            return from(authService.getRefreshToken()).pipe(
              switchMap(refreshToken => {
                if (!refreshToken) {
                  doLogout(authService, router);
                  return throwError(() => err);
                }

                if (isRefreshing) {
                  return refreshDone$.pipe(
                    filter(t => t !== null),
                    take(1),
                    switchMap(newToken =>
                      next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
                    )
                  );
                }

                isRefreshing = true;
                refreshDone$.next(null);

                return from(authService.refreshTokens()).pipe(
                  switchMap(res => {
                    isRefreshing = false;
                    refreshDone$.next(res.token);
                    return next(req.clone({ setHeaders: { Authorization: `Bearer ${res.token}` } }));
                  }),
                  catchError(refreshErr => {
                    isRefreshing = false;
                    refreshDone$.next(null);
                    doLogout(authService, router);
                    return throwError(() => refreshErr);
                  })
                );
              })
            );
          }
          return throwError(() => err);
        })
      );
    })
  );
};

function doLogout(authService: AuthService, router: Router): void {
  authService.resetLoginState();
  authService.logout();
  router.navigate(['login'], { replaceUrl: true });
}
